import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import './Unsubscribe.css';

interface UnsubscribeResponse {
  success: boolean;
  message: string;
  error?: string;
}

const Unsubscribe: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'invalid'>('loading');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(5);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage('Invalid unsubscribe link. Missing token parameter.');
      return;
    }

    handleUnsubscribe();
  }, [token]);

  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/');
    }
  }, [status, countdown, navigate]);

  const handleUnsubscribe = async () => {
    try {
      const response = await fetch('https://us-central1-trinity-452312.cloudfunctions.net/thekults-data-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          action: 'unsubscribe',
          token 
        }),
      });

      const data: UnsubscribeResponse = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message);
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('An error occurred while processing your request. Please try again later.');
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="status-icon loading" size={48} />;
      case 'success':
        return <CheckCircle className="status-icon success" size={48} />;
      case 'error':
        return <XCircle className="status-icon error" size={48} />;
      case 'invalid':
        return <AlertCircle className="status-icon invalid" size={48} />;
      default:
        return null;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'loading':
        return 'Processing Unsubscribe Request...';
      case 'success':
        return 'Successfully Unsubscribed';
      case 'error':
        return 'Unsubscribe Failed';
      case 'invalid':
        return 'Invalid Unsubscribe Link';
      default:
        return '';
    }
  };

  return (
    <div className="unsubscribe-page">
      <div className="unsubscribe-container">
        <div className="unsubscribe-content">
          {getStatusIcon()}
          
          <h1 className="unsubscribe-title">{getStatusTitle()}</h1>
          
          <div className="unsubscribe-message">
            <p>{message}</p>
            
            {status === 'success' && (
              <div className="success-details">
                <p>Your data has been successfully deleted from our systems.</p>
                <p>You will be redirected to the home page in {countdown} seconds...</p>
              </div>
            )}
            
            {status === 'error' && (
              <div className="error-details">
                <p>If you continue to experience issues, please contact us directly.</p>
                <a href="/contact" className="contact-link">Contact Support</a>
              </div>
            )}
            
            {status === 'invalid' && (
              <div className="invalid-details">
                <p>Please check your email for the correct unsubscribe link.</p>
                <p>If you need help, please contact us.</p>
                <a href="/contact" className="contact-link">Contact Support</a>
              </div>
            )}
          </div>
          
          <div className="unsubscribe-actions">
            {status === 'success' && (
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/')}
              >
                Go to Home Page
              </button>
            )}
            
            {status === 'error' && (
              <button 
                className="btn btn-secondary"
                onClick={handleUnsubscribe}
              >
                Try Again
              </button>
            )}
            
            {status === 'invalid' && (
              <button 
                className="btn btn-secondary"
                onClick={() => navigate('/')}
              >
                Go to Home Page
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe; 